import { Cable, Folder } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Grid, Stack, Typography } from '@mui/material';
import { sortBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';
import { EntityPickerPopover } from '@work4all/components/lib/components/entity-picker/components';
import { FixedDataPicker } from '@work4all/components/lib/components/entity-picker/fixed-data-picker/FixedDataPicker';
import { Divider } from '@work4all/components/lib/dataDisplay/divider/Divider';
import { LabeledInput } from '@work4all/components/lib/input/labeled-input';
import { PickerTargetButton } from '@work4all/components/lib/input/picker-target-button';

import { OneDriveFspConfig } from '@work4all/models/lib/Classes/OneDriveFspConfig.entity';

import { useFormContextPlus } from '../../../../../form-plus/use-form-context-plus';
import { FileServiceProviderConfigFormValue } from '../../FileServiceProviderConfigOverlayController';
import {
  SharePointDrive,
  SharePointSite,
  useFSPAccess,
} from '../../hooks/use-fsp-access';
import { FailAlert } from '../fail-alert/FailAlert';
import { SuccessAlert } from '../sucess-alert/SuccessAlert';

export const SharePoint365Conf = () => {
  const { t } = useTranslation();
  const { watch, setValue } =
    useFormContextPlus<FileServiceProviderConfigFormValue>();

  const dialogs = useDialogs();

  const config: OneDriveFspConfig = watch('config');
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState<SharePointSite[]>([]);
  const [drives, setDrives] = useState<SharePointDrive[]>([]);

  const {
    testOneDriveConfig,
    listFileServiceProviderConfigRootItems,
    listSharePointSites,
    listSharePointDrives,
  } = useFSPAccess();

  const refreshSites = useCallback(async () => {
    const result = await listSharePointSites(config);
    setSites(sortBy(result.data.listSharePointSites, 'name'));
  }, [config, listSharePointSites]);

  const refreshDrives = useCallback(async () => {
    const result = await listSharePointDrives(config);
    setDrives(sortBy(result.data.listSharePointDrives, 'name'));
  }, [config, listSharePointDrives]);

  const handleListRootItemsClick = useCallback(async () => {
    const result = await listFileServiceProviderConfigRootItems(config);
    const rootItems = sortBy(
      result.data.listFileServiceProviderConfigRootItems,
      'key'
    );

    dialogs.alert({
      title: t('COMMON.FSP.LIST_ROOT_ITEMS.TITLE'),
      description: (
        <table width="100%">
          {rootItems.map((item) => (
            <tr key={item.key}>
              <td>
                {item.itemType === 'FOLDER' ? (
                  <Folder color="disabled" />
                ) : null}
              </td>
              <td width="100%">
                <Typography>{item.key}</Typography>
              </td>
            </tr>
          ))}
        </table>
      ),
    });
  }, [config, dialogs, listFileServiceProviderConfigRootItems, t]);

  const handleTestClick = useCallback(async () => {
    setLoading(true);

    try {
      const result = await testOneDriveConfig(config);
      dialogs.alert({
        title: t('COMMON.FSP.CONNECTION_TEST.TITLE'),
        description: result.data.testFileServiceProviderConfig ? (
          <SuccessAlert />
        ) : (
          <FailAlert />
        ),
      });

      if (result.data.testFileServiceProviderConfig) {
        refreshSites();
        refreshDrives();
      }
    } catch (err) {
      console.warn(err);
      dialogs.alert({
        title: t('COMMON.FSP.CONNECTION_TEST.TITLE'),
        description: <FailAlert />,
      });
    }

    setLoading(false);
  }, [config, dialogs, refreshDrives, refreshSites, t, testOneDriveConfig]);

  const handleChange = useCallback(
    (value: OneDriveFspConfig) => {
      setValue(
        'config',
        { ...config, ...value },
        { shouldDirty: true, shouldValidate: true }
      );
    },
    [config, setValue]
  );

  useEffect(() => {
    if (config?.clientId && config?.tenantId && config?.clientSecret) {
      refreshSites();
      refreshDrives();
    }
  }, []);

  useEffect(() => {
    if (config?.siteId) {
      refreshDrives();
    }
  }, [config?.siteId, refreshDrives]);

  const siteData = useMemo(() => {
    return sites.map((site) => ({
      id: site.id,
      name: site.name,
      url: site.url,
    }));
  }, [sites]);

  const driveData = useMemo(() => {
    return drives.map((drive) => ({
      id: drive.id,
      name: drive.name,
      url: drive.url,
    }));
  }, [drives]);

  const site = useMemo(() => {
    return sites.find((site) => site.id === config?.siteId);
  }, [config?.siteId, sites]);

  const drive = useMemo(() => {
    return drives.find((drive) => drive.id === config?.driveId);
  }, [config?.driveId, drives]);

  return (
    <Grid container spacing="1rem">
      <Grid item xs={12} sm={12} md={4}>
        <LabeledInput
          label={t('COMMON.CLIENT_ID')}
          value={config?.clientId}
          type="password"
          onChange={(e) => handleChange({ clientId: e.target.value })}
          inputProps={{
            autocomplete: 'new-password',
          }}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={4}>
        <LabeledInput
          label={t('COMMON.TENANT_ID')}
          value={config?.tenantId}
          type="password"
          onChange={(e) => handleChange({ tenantId: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={4}>
        <LabeledInput
          label={t('COMMON.CLIENT_SECRET')}
          value={config?.clientSecret}
          type="password"
          onChange={(e) => handleChange({ clientSecret: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12}>
        <LoadingButton
          onClick={handleTestClick}
          startIcon={<Cable />}
          loading={loading}
          disabled={
            !config?.clientId || !config?.tenantId || !config?.clientSecret
          }
        >
          {t('COMMON.FSP.CONNECTION_TEST.TITLE')}
        </LoadingButton>
      </Grid>
      <Grid item xs={12} sm={12} md={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <Stack spacing="1rem">
          <EntityPickerPopover
            picker={
              <FixedDataPicker
                value={
                  siteData.find((x) => x.id === config?.siteId) || {
                    id: config?.siteId,
                    name: config?.siteId,
                  }
                }
                onChange={(opt) => {
                  handleChange({ driveId: '', siteId: opt.id.toString() });
                }}
                dataSet={siteData}
                renderItemContent={(item) => (
                  <Typography>{item.name}</Typography>
                )}
                multiple={false}
                layout="advanced"
              />
            }
          >
            <PickerTargetButton
              style={{ padding: 0 }}
              label={t('COMMON.SITE')}
              value={site?.name}
            />
          </EntityPickerPopover>
          <LabeledInput
            label={t('COMMON.SITE_ID')}
            value={config?.siteId}
            onChange={(e) => {
              handleChange({ driveId: '', siteId: e.target.value });
            }}
          />
        </Stack>
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <Stack spacing="1rem">
          <EntityPickerPopover
            picker={
              <FixedDataPicker
                value={
                  driveData.find((x) => x.id === config?.driveId) || {
                    id: config?.driveId,
                    name: config?.driveId,
                  }
                }
                onChange={(opt) => {
                  handleChange({ driveId: opt.id.toString() });
                }}
                dataSet={driveData}
                renderItemContent={(item) => (
                  <Typography>{item.name}</Typography>
                )}
                multiple={false}
                layout="advanced"
              />
            }
          >
            <PickerTargetButton
              style={{ padding: 0 }}
              label={t('COMMON.DRIVE')}
              value={drive?.name}
            />
          </EntityPickerPopover>
          <LabeledInput
            label={t('COMMON.DRIVE_ID')}
            value={config?.driveId}
            onChange={(e) => {
              handleChange({ driveId: e.target.value });
            }}
          />
        </Stack>
      </Grid>
      <Grid item xs={12} sm={12} md={12}>
        <LoadingButton
          onClick={handleListRootItemsClick}
          startIcon={<Folder />}
          loading={loading}
          disabled={
            !config?.clientId ||
            !config?.tenantId ||
            !config?.clientSecret ||
            !config?.siteId ||
            !config?.driveId
          }
        >
          {t('COMMON.FSP.LIST_ROOT_ITEMS.TITLE')}
        </LoadingButton>
      </Grid>
    </Grid>
  );
};
